import { WorkSanctionsPageStore } from './work-sanctions-page.store';
import { Component, computed, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { getWorksFrag } from './work-sanctions-page.query';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { Router } from '@angular/router';
import { WorkGridItemObject } from '@it2go/types';
import { WorkDataSanctionObject } from '@libs/types/src/services/object-type/work/work-data-sanction.object';
import moment from 'moment';
import { TranslatePipe } from '@ngx-translate/core';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import {
    RealizedWorkPageHeaderComponent,
} from '../../../../../modules/content/pages/services/realized-work-pages/realized-work-page-header/realized-work-page-header.component';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import {
    WorkUniqueDaysPipe,
    WorkUniquesPipe,
} from '../../../../../modules/content/components/pipe/work/work-uniques.pipe';
import { ConfirmBuilder } from '../../../../../modules/shared/service/confirm.builder';
import { fb, routeParams } from '../../../../../modules/shared/shared.global-service';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { TableColumn, TableColumnType } from '../../../../../modules/shared/components/kit/table/table.types';
import { folderPersonColumn, usersChipColumn } from '../../../../../modules/shared/components/kit/table/table.fields';
import { workUri } from '../../../../../utils/router.utils';
import { successDeleted } from '../../../../../modules/content/content.error';
import { workHideDelete } from '../../../../medical-service/work/work-detail/utils/work.transform-utils';
import { WorkPrintService } from '../../../../medical-service/work/work-print.service';

@Component({
    selector: 'app-work-sanctions-page',
    templateUrl: './work-sanctions-page.component.html',
    styleUrl: './work-sanctions-page.component.scss',
    providers: [WorkSanctionsPageStore],
    imports: [
        TranslatePipe,
        PrimeTemplate,
        RealizedWorkPageHeaderComponent,
        TableComponent,
        ButtonComponent,
        WorkUniqueDaysPipe,
        WorkUniquesPipe,
    ],
})
export class WorkSanctionsPageComponent {

    protected readonly store = inject(WorkSanctionsPageStore);
    protected readonly router = inject(Router);
    protected readonly confirm = inject(ConfirmBuilder);
    protected readonly workPrint = inject(WorkPrintService);

    protected readonly tableComponent = viewChild(TableComponent);

    protected form = fb.group({
        date: fb.control(moment().startOf('d').toDate()),
        endDate: fb.control<Date>(new Date()),
        selectedVisits: fb.control<string>('all'),
        selectedPeriod: fb.control<TimePeriodEnum>(TimePeriodEnum.Day, {
            nonNullable: true,
        }),
    });

    protected isInFolder = computed(() => this.router.url.includes('/folders'));
    protected dateFilter: WritableSignal<any> = signal([]);
    protected workplaceFilter: WritableSignal<any> = signal([]);
    protected additionalFilter = computed(() => [
        [
            {
                value: [this.store.globalGlobalStore.organizationStore.selectedId()],
                column: 'organizationId',
                operator: 'EQ',
            },
        ],
        [
            {
                value: [WorkTypeEnum.SANCTION],
                column: 'workType',
                operator: 'EQ',
            },
        ],
        ...(this.isInFolder() ? [] : this.dateFilter()),
        ...(this.isInFolder() ? [] : this.workplaceFilter()),
        ...(this.isInFolder() ? [
            {
                value: [routeParams.folderId()],
                column: 'folderId',
                operator: 'IN',
            },
        ] : []),
    ]);

    protected columns = computed<TableColumn[]>(() => {
        return [
            { name: 'print' },
            {
                name: 'workData.swds.sequenceNumber',
                dataKey: null,
                data: (item: WorkGridItemObject<WorkDataSanctionObject>) => `${String(item.workData?.sequenceNumber || 0)}/${new Date(item.workData?.date || new Date()).getFullYear()}`,
                sortable: true,
            },
            {
                name: 'workData.swds.data',
                dataKey: 'workData.data',
                type: TableColumnType.String50,
            },
            {
                name: 'workData.swds.reason',
                dataKey: 'workData.reason',
                type: TableColumnType.String50,
            },
            {
                name: 'workData.swds.comment',
                dataKey: 'workData.comment',
                type: TableColumnType.String50,
            },
            usersChipColumn,
            ...(this.isInFolder() ? [] : [folderPersonColumn]),
        ];
    });

    protected async detail(item: WorkGridItemObject): Promise<void> {
        await this.router.navigateByUrl(workUri(item, item.serviceId));
    }

    protected delete(event: any): void {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteWork({ id: event.item.id });
                this.tableComponent()?.reload();
                successDeleted();
            },
            event,
        );
    }

    protected print(work: WorkGridItemObject<WorkDataSanctionObject>): void {
        this.workPrint.openRow(work);
    }

    protected readonly workHideDelete = workHideDelete(this.store.globalGlobalStore);

    protected readonly getWorksFrag = getWorksFrag;
}
